import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../hooks/useHasPermission";
import { actionTypesAPI, employeesAPI, replyActionsAPI } from "../services/api";
import { PERMISSIONS } from "../utils/permissions";

const emptyRow = (replyDate) => ({
  key: `${Date.now()}-${Math.random()}`,
  action_id: null,
  action_type: "",
  action_date: replyDate ? dayjs(replyDate) : dayjs(),
  employee_no: "",
  employee_name: "",
  employeeFound: false,
  employeeMissing: false,
  note: "",
});

const fieldSx = {
  "& .MuiOutlinedInput-root": { fontSize: "0.95rem" },
  "& .MuiInputLabel-root": { fontSize: "0.95rem" },
};

const ActionRowFields = ({
  row,
  actionTypes,
  typeName,
  t,
  canManageEmployees,
  canDeleteAction,
  onUpdate,
  onLookup,
  onQuickAdd,
  onRemoveUnsaved,
  onRequestDeleteSaved,
  layout = "table",
}) => {
  const saved = Boolean(row.action_id);
  const canRemove = !saved || canDeleteAction;

  const actionTypeField = (
    <FormControl fullWidth size="small" sx={fieldSx} disabled={saved}>
      <InputLabel>{t("replyActions.actionType")}</InputLabel>
      <Select
        label={t("replyActions.actionType")}
        value={row.action_type}
        onChange={(event) => onUpdate(row.key, { action_type: event.target.value })}
      >
        {actionTypes.map((item) => (
          <MenuItem key={item.action_type_id} value={item.action_type_id}>
            {typeName(item)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateField = (
    <DateTimePicker
      format="DD/MM/YYYY HH:mm"
      value={row.action_date}
      disabled={saved}
      onChange={(value) => onUpdate(row.key, { action_date: value })}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          label: t("replyActions.actionDate"),
          sx: fieldSx,
        },
      }}
    />
  );

  const employeeNoField = (
    <TextField
      size="small"
      fullWidth
      label={t("employees.employeeNo")}
      value={row.employee_no}
      disabled={saved}
      onChange={(event) =>
        onUpdate(row.key, {
          employee_no: event.target.value.replace(/[^0-9]/g, ""),
          employeeFound: false,
          employeeMissing: false,
          employee_name: "",
        })
      }
      onBlur={() => onLookup(row)}
      inputProps={{ inputMode: "numeric" }}
      sx={fieldSx}
    />
  );

  const employeeNameField = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TextField
        size="small"
        fullWidth
        label={t("replyActions.employeeName")}
        value={row.employee_name}
        placeholder={
          row.employeeMissing
            ? t("replyActions.employeeNotFound")
            : t("replyActions.employeeName")
        }
        error={row.employeeMissing}
        InputProps={{ readOnly: true }}
        sx={fieldSx}
      />
      {row.employeeMissing && canManageEmployees && (
        <IconButton
          color="primary"
          onClick={() => onQuickAdd(row)}
          aria-label={t("replyActions.quickAdd")}
        >
          <AddIcon />
        </IconButton>
      )}
    </Box>
  );

  const noteField = (
    <TextField
      size="small"
      fullWidth
      label={t("replyActions.note")}
      value={row.note}
      disabled={saved}
      onChange={(event) => onUpdate(row.key, { note: event.target.value })}
      sx={fieldSx}
    />
  );

  const deleteControl =
    canRemove ? (
      layout === "card" ? (
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() =>
            saved ? onRequestDeleteSaved(row) : onRemoveUnsaved(row.key)
          }
          fullWidth
        >
          {t("replyActions.deleteRow")}
        </Button>
      ) : (
        <IconButton
          color="error"
          onClick={() =>
            saved ? onRequestDeleteSaved(row) : onRemoveUnsaved(row.key)
          }
          aria-label={t("replyActions.deleteRow")}
        >
          <DeleteIcon />
        </IconButton>
      )
    ) : null;

  if (layout === "card") {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>{actionTypeField}</Box>
          <Box>{dateField}</Box>
          <Box>{employeeNoField}</Box>
          <Box>{employeeNameField}</Box>
          <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>{noteField}</Box>
          {deleteControl && (
            <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>{deleteControl}</Box>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <TableRow>
      <TableCell sx={{ minWidth: 180, verticalAlign: "top" }}>{actionTypeField}</TableCell>
      <TableCell sx={{ minWidth: 220, verticalAlign: "top" }}>{dateField}</TableCell>
      <TableCell sx={{ minWidth: 140, verticalAlign: "top" }}>{employeeNoField}</TableCell>
      <TableCell sx={{ minWidth: 200, verticalAlign: "top" }}>{employeeNameField}</TableCell>
      <TableCell sx={{ minWidth: 160, verticalAlign: "top" }}>{noteField}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{deleteControl}</TableCell>
    </TableRow>
  );
};

const ReplyActionsDialog = ({ open, onClose, reply, ticketId, onSaved }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const useCards = useMediaQuery(theme.breakpoints.down("lg"));
  const canManageEmployees = useHasPermission(PERMISSIONS.MANAGE_EMPLOYEES);
  const canDeleteAction = useHasPermission(PERMISSIONS.DELETE_REPLY_ACTION);
  const replyDate = reply?.reply_date || reply?.created_at;
  const replyId = reply?.reply_id || reply?.id;

  const [rows, setRows] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddRowKey, setQuickAddRowKey] = useState(null);
  const [quickAdd, setQuickAdd] = useState({
    employee_no: "",
    name_ar: "",
    name_en: "",
  });
  const [quickAddError, setQuickAddError] = useState("");
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!open || !replyId) return;
    setLoading(true);
    setError("");
    try {
      const [typesResponse, actionsResponse] = await Promise.all([
        actionTypesAPI.list({ page_size: 200 }),
        replyActionsAPI.list({ reply: replyId }),
      ]);
      const types = Array.isArray(typesResponse)
        ? typesResponse
        : typesResponse.results || [];
      setActionTypes(types.filter((item) => item.is_active !== false));

      const existing = Array.isArray(actionsResponse)
        ? actionsResponse
        : actionsResponse.results || [];
      if (existing.length) {
        setRows(
          existing.map((item) => ({
            key: `existing-${item.action_id}`,
            action_id: item.action_id,
            action_type: item.action_type,
            action_date: item.action_date ? dayjs(item.action_date) : dayjs(replyDate),
            employee_no: String(item.employee_no || item.employee || ""),
            employee_name:
              (isRTL ? item.employee_name_ar : item.employee_name_en) ||
              item.employee_name_ar ||
              "",
            employeeFound: true,
            employeeMissing: false,
            note: item.note || "",
          }))
        );
      } else {
        setRows([emptyRow(replyDate)]);
      }
    } catch (err) {
      setError(err.message || t("replyActions.loadError"));
      setRows([emptyRow(replyDate)]);
    } finally {
      setLoading(false);
    }
  }, [open, replyId, replyDate, isRTL, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const lookupEmployee = async (row) => {
    const raw = String(row.employee_no || "").trim();
    if (!raw) {
      updateRow(row.key, { employee_name: "", employeeFound: false, employeeMissing: false });
      return;
    }
    if (!/^[1-9][0-9]*$/.test(raw)) {
      updateRow(row.key, {
        employee_name: "",
        employeeFound: false,
        employeeMissing: true,
      });
      return;
    }
    try {
      const employee = await employeesAPI.get(raw);
      const name = isRTL
        ? employee.name_ar || employee.name_en
        : employee.name_en || employee.name_ar;
      updateRow(row.key, {
        employee_name: name,
        employeeFound: true,
        employeeMissing: false,
      });
    } catch {
      updateRow(row.key, {
        employee_name: "",
        employeeFound: false,
        employeeMissing: true,
      });
    }
  };

  const handleSave = async () => {
    const validRows = rows.filter(
      (row) => !row.action_id && row.action_type && row.employeeFound && row.employee_no
    );
    if (!validRows.length) {
      setError(t("replyActions.noValidRows"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await replyActionsAPI.saveBulk({
        reply: replyId,
        ticket: Number(ticketId),
        actions: validRows.map((row) => ({
          action_type: row.action_type,
          employee: Number(row.employee_no),
          action_date: row.action_date?.isValid?.()
            ? row.action_date.toISOString()
            : dayjs(replyDate).toISOString(),
          note: row.note || "",
        })),
      });
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch (err) {
      setError(err.message || t("replyActions.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAdd = async () => {
    if (!/^[1-9][0-9]*$/.test(String(quickAdd.employee_no).trim())) {
      setQuickAddError(t("employees.invalidNumber"));
      return;
    }
    if (!quickAdd.name_ar.trim()) {
      setQuickAddError(t("referencePages.fieldRequired"));
      return;
    }
    setQuickAddError("");
    try {
      const created = await employeesAPI.create({
        employee_no: Number(quickAdd.employee_no),
        name_ar: quickAdd.name_ar.trim(),
        name_en: quickAdd.name_en.trim(),
        is_active: true,
      });
      if (quickAddRowKey) {
        const name = isRTL
          ? created.name_ar || created.name_en
          : created.name_en || created.name_ar;
        updateRow(quickAddRowKey, {
          employee_no: String(created.employee_no),
          employee_name: name,
          employeeFound: true,
          employeeMissing: false,
        });
      }
      setQuickAddOpen(false);
    } catch (err) {
      setQuickAddError(err.message);
    }
  };

  const removeUnsaved = (key) => {
    setRows((prev) => {
      const next = prev.filter((item) => item.key !== key);
      return next.length ? next : [emptyRow(replyDate)];
    });
  };

  const confirmDeleteSaved = async () => {
    if (!rowToDelete?.action_id) {
      setRowToDelete(null);
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await replyActionsAPI.delete(rowToDelete.action_id);
      setRows((prev) => {
        const next = prev.filter((item) => item.key !== rowToDelete.key);
        return next.length ? next : [emptyRow(replyDate)];
      });
      setRowToDelete(null);
      if (onSaved) {
        onSaved();
      }
    } catch (err) {
      setError(err.message || t("replyActions.deleteError"));
      setRowToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const typeName = (item) => (isRTL ? item.name_ar : item.name_en) || item.name_ar;

  const rowProps = {
    actionTypes,
    typeName,
    t,
    canManageEmployees,
    canDeleteAction,
    onUpdate: updateRow,
    onLookup: lookupEmployee,
    onQuickAdd: (row) => {
      setQuickAddRowKey(row.key);
      setQuickAdd({
        employee_no: row.employee_no,
        name_ar: "",
        name_en: "",
      });
      setQuickAddError("");
      setQuickAddOpen(true);
    },
    onRemoveUnsaved: removeUnsaved,
    onRequestDeleteSaved: setRowToDelete,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !saving && onClose()}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        dir={isRTL ? "rtl" : "ltr"}
        PaperProps={{
          sx: {
            maxHeight: { xs: "100%", sm: "90vh" },
          },
        }}
      >
        <DialogTitle>{t("replyActions.title")}</DialogTitle>
        <DialogContent
          sx={{
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : useCards ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              {rows.map((row) => (
                <ActionRowFields
                  key={row.key}
                  row={row}
                  layout="card"
                  {...rowProps}
                />
              ))}
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("replyActions.actionType")}</TableCell>
                  <TableCell>{t("replyActions.actionDate")}</TableCell>
                  <TableCell>{t("employees.employeeNo")}</TableCell>
                  <TableCell>{t("replyActions.employeeName")}</TableCell>
                  <TableCell>{t("replyActions.note")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <ActionRowFields
                    key={row.key}
                    row={row}
                    layout="table"
                    {...rowProps}
                  />
                ))}
              </TableBody>
            </Table>
          )}
          <Button
            sx={{ mt: 2 }}
            startIcon={<AddIcon />}
            onClick={() => setRows((prev) => [...prev, emptyRow(replyDate)])}
          >
            {t("replyActions.addRow")}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
            {saving ? <CircularProgress size={22} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("replyActions.quickAdd")}</DialogTitle>
        <DialogContent>
          {quickAddError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {quickAddError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label={t("employees.employeeNo")}
              value={quickAdd.employee_no}
              onChange={(event) =>
                setQuickAdd((prev) => ({
                  ...prev,
                  employee_no: event.target.value.replace(/[^0-9]/g, ""),
                }))
              }
            />
            <TextField
              label={t("referencePages.nameAr")}
              value={quickAdd.name_ar}
              onChange={(event) =>
                setQuickAdd((prev) => ({ ...prev, name_ar: event.target.value }))
              }
              required
            />
            <TextField
              label={t("referencePages.nameEn")}
              value={quickAdd.name_en}
              onChange={(event) =>
                setQuickAdd((prev) => ({ ...prev, name_en: event.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAddOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleQuickAdd}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rowToDelete)}
        onClose={() => !deleting && setRowToDelete(null)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("replyActions.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("replyActions.deleteConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRowToDelete(null)} disabled={deleting}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteSaved}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={22} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReplyActionsDialog;
